import { StripeProvider } from "@/components/stripe/StripeProvider";
import { StripePaymentForm } from "@/components/subscription/StripePaymentForm";
import { SubscriptionModal } from "@/components/subscription/SubscriptionModal";
import type {
	Invoice,
	PaymentMethod,
	SubscriptionPlan,
	SubscriptionUsage,
	UserSubscription,
} from "@/models/subscription/subscription.model";
import { SubscriptionStatus } from "@/models/subscription/subscription.model";
import {
	addPaymentMethod,
	cancelSubscription,
	getCurrentSubscription,
	getInvoices,
	getLocationPricing,
	getPaymentMethods,
	getSubscriptionPlans,
	getSubscriptionUsage,
	reactivateSubscription,
	removePaymentMethod,
	updateSubscriptionPaymentMethod,
	updateSubscriptionPlan,
} from "@/services/subscription-service";
import { getUserSelf } from "@/services/user-service";
import { useUserStore } from "@/stores/user-store";
import { updateSubscriptionStatus } from "@/utils/update-subscription-status";
import {
	CreditCardOutlined,
	CrownOutlined,
	DownloadOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	LinkOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Modal,
	Progress,
	Row,
	Space,
	Spin,
	Table,
	Tag,
	Typography,
	message,
} from "antd";
import React, { useEffect, useState } from "react";

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

export const SubscriptionTab: React.FC = () => {
	const { user, setUser } = useUserStore();
	const [loading, setLoading] = useState(true);
	const [subscription, setSubscription] = useState<UserSubscription | null>(
		null,
	);
	const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
	const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	const [actionLoading, setActionLoading] = useState(false);
	const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
		null,
	);
	const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
	const [planChangeModalOpen, setPlanChangeModalOpen] = useState(false);
	// State for location count (same for all plans)
	// This stores just the location count, pricing is calculated per plan
	const [locationCount, setLocationCount] = useState<number>(1);

	useEffect(() => {
		loadSubscriptionData();
	}, [user?.organization_id]);

	const loadSubscriptionData = async () => {
		try {
			setLoading(true);
			const [
				subscriptionData,
				plansData,
				usageData,
				invoicesData,
				paymentMethodsData,
			] = await Promise.allSettled([
				getCurrentSubscription(),
				getSubscriptionPlans(),
				getSubscriptionUsage(),
				getInvoices(),
				getPaymentMethods(),
			]);

			if (subscriptionData.status === "fulfilled") {
				setSubscription(subscriptionData.value);
			} else {
				console.error("Failed to load subscription:", subscriptionData.reason);
			}

			if (plansData.status === "fulfilled") {
				setPlans(plansData.value);
				// Load location count for pricing calculations
				// Don't await - let it run in background, errors are handled internally
				loadLocationCount().catch((error) => {
					console.error("Failed to load location count:", error);
				});
			} else {
				console.error("Failed to load plans:", plansData.reason);
			}

			if (usageData.status === "fulfilled") {
				setUsage(usageData.value);
			} else {
				console.error("Failed to load usage:", usageData.reason);
			}

			if (invoicesData.status === "fulfilled") {
				setInvoices(invoicesData.value);
			} else {
				console.error("Failed to load invoices:", invoicesData.reason);
			}

			if (paymentMethodsData.status === "fulfilled") {
				setPaymentMethods(paymentMethodsData.value);
			} else {
				const error = paymentMethodsData.reason;
				console.error("Failed to load payment methods:", error);
				// Don't show error message to user for payment methods failure
				// It's not critical - user can still use the subscription tab
			}
		} catch (error) {
			console.error("Failed to load subscription data:", error);
			message.error("Failed to load subscription data");
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Load location count for pricing calculations
	 *
	 * This function fetches the user's location count which is used to calculate
	 * pricing for each plan individually (Base: $25 × locations, Plus: $50 × locations).
	 */
	const loadLocationCount = async () => {
		try {
			const pricing = await getLocationPricing();
			setLocationCount(pricing.locationCount);
		} catch (error) {
			console.error("Failed to load location count:", error);
			// Set fallback to 1 location if API call fails
			setLocationCount(1);
		}
	};

	/**
	 * Calculate pricing for a specific plan
	 *
	 * @param plan - The subscription plan to calculate pricing for
	 * @returns Object with formatted pricing information
	 */
	const calculatePlanPricing = (plan: SubscriptionPlan) => {
		const pricePerLocation = plan.price; // Price in cents per location
		const totalPrice = pricePerLocation * locationCount;

		// Format numbers with commas for thousands
		const formatCurrency = (cents: number) => {
			const dollars = (cents / 100).toFixed(2);
			const parts = dollars.split(".");
			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
			return `$${parts.join(".")}`;
		};

		return {
			pricePerLocation,
			totalPrice,
			formattedPricePerLocation: formatCurrency(pricePerLocation),
			formattedTotalPrice: formatCurrency(totalPrice),
		};
	};

	const handleCancelSubscription = () => {
		confirm({
			title: "Cancel Subscription",
			icon: <ExclamationCircleOutlined />,
			content:
				"Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.",
			okText: "Yes, Cancel",
			okType: "danger",
			cancelText: "No, Keep Subscription",
			onOk: async () => {
				try {
					setActionLoading(true);
					const updatedSubscription = await cancelSubscription(true);
					setSubscription(updatedSubscription);
					message.success("Subscription canceled successfully");
				} catch (error) {
					message.error("Failed to cancel subscription");
				} finally {
					setActionLoading(false);
				}
			},
		});
	};

	const handleReactivateSubscription = async () => {
		try {
			setActionLoading(true);
			const updatedSubscription = await reactivateSubscription();
			setSubscription(updatedSubscription);
			message.success("Subscription reactivated successfully");

			// Refresh all subscription data
			await loadSubscriptionData();

			// Update user store with new subscription status (including hasPlusSubscription)
			const currentUser = useUserStore.getState().user;
			await updateSubscriptionStatus(currentUser);
		} catch (error) {
			message.error("Failed to reactivate subscription");
		} finally {
			setActionLoading(false);
		}
	};

	const getStatusColor = (status: SubscriptionStatus) => {
		switch (status) {
			case SubscriptionStatus.ACTIVE:
				return "green";
			case SubscriptionStatus.TRIALING:
				return "blue";
			case SubscriptionStatus.CANCELED:
				return "orange";
			case SubscriptionStatus.PAST_DUE:
				return "red";
			default:
				return "default";
		}
	};

	const getCurrentPlan = () => {
		if (!subscription) return null;
		return plans.find((plan) => plan.id === subscription.planId);
	};

	const handleSelectPlan = (plan: SubscriptionPlan) => {
		setSelectedPlan(plan);
		setSubscriptionModalOpen(true);
	};

	const handleSubscriptionSuccess = async () => {
		try {
			// Refresh subscription data
			await loadSubscriptionData();

			// Refresh user data to update trial status
			const updatedUser = await getUserSelf();
			setUser(updatedUser);

			// Update user store with new subscription status (including hasPlusSubscription)
			await updateSubscriptionStatus(updatedUser);

			message.success("Your account has been upgraded successfully!");
		} catch (error) {
			console.error("Failed to refresh user data after subscription:", error);
			message.error(
				"Subscription created but failed to refresh account status. Please refresh the page.",
			);
		}
	};

	const handleAddPaymentMethod = () => {
		setPaymentMethodModalOpen(true);
	};

	const handlePaymentMethodAdded = async (paymentMethodId: string) => {
		try {
			setActionLoading(true);

			// Always set new payment methods as default (user likely wants to use the new card)
			await addPaymentMethod(paymentMethodId, true);

			// Update the subscription to use this payment method as default
			if (subscription?.stripeSubscriptionId) {
				try {
					// Update subscription's default payment method
					await updateSubscriptionPaymentMethod(
						subscription.stripeSubscriptionId,
						paymentMethodId,
					);
				} catch (error) {
					console.warn("Failed to update subscription payment method:", error);
					// Don't fail the whole operation if this fails
				}
			}

			await loadSubscriptionData(); // Refresh payment methods
			setPaymentMethodModalOpen(false);
			message.success(
				"Payment method added and set as default for your subscription!",
			);
		} catch (error) {
			console.error("Failed to add payment method:", error);
			message.error("Failed to add payment method. Please try again.");
		} finally {
			setActionLoading(false);
		}
	};

	const handleRemovePaymentMethod = async (paymentMethodId: string) => {
		try {
			setActionLoading(true);
			await removePaymentMethod(paymentMethodId);
			await loadSubscriptionData(); // Refresh payment methods
			message.success("Payment method removed successfully!");
		} catch (error) {
			console.error("Failed to remove payment method:", error);
			message.error("Failed to remove payment method. Please try again.");
		} finally {
			setActionLoading(false);
		}
	};

	const handleChangePlan = () => {
		setPlanChangeModalOpen(true);
	};

	const handlePlanChange = async (newPlan: SubscriptionPlan) => {
		const currentPlan = getCurrentPlan();
		if (!currentPlan) {
			message.error("Unable to determine current plan");
			return;
		}

		if (newPlan.id === currentPlan.id) {
			message.info("You are already on this plan");
			setPlanChangeModalOpen(false);
			return;
		}

		confirm({
			title: "Change Subscription Plan",
			icon: <ExclamationCircleOutlined />,
			content: (
				<div>
					<p>Are you sure you want to change your plan?</p>
					<div
						style={{
							margin: "16px 0",
							padding: "12px",
							backgroundColor: "#f5f5f5",
							borderRadius: "6px",
						}}
					>
						<div>
							<strong>Current Plan:</strong> {currentPlan.name} - $
							{(currentPlan.price / 100).toFixed(2)}/location/month
						</div>
						<div>
							<strong>New Plan:</strong> {newPlan.name} - $
							{(newPlan.price / 100).toFixed(2)}/location/month
						</div>
					</div>
					<p style={{ fontSize: "14px", color: "#666" }}>
						The change will take effect immediately and you'll be
						charged/credited the prorated difference.
					</p>
				</div>
			),
			okText: "Yes, Change Plan",
			okType: "primary",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					setActionLoading(true);
					const updatedSubscription = await updateSubscriptionPlan(
						newPlan.id.toString(),
					);
					setSubscription(updatedSubscription);
					setPlanChangeModalOpen(false);
					message.success(`Successfully changed to ${newPlan.name} plan!`);

					// Refresh all subscription data
					await loadSubscriptionData();

					// Update user store with new subscription status (including hasPlusSubscription)
					await updateSubscriptionStatus(user);
				} catch (error) {
					console.error("Failed to change plan:", error);
					message.error("Failed to change plan. Please try again.");
				} finally {
					setActionLoading(false);
				}
			},
		});
	};

	const invoiceColumns = [
		{
			title: "Invoice #",
			dataIndex: "invoiceNumber",
			key: "invoiceNumber",
			render: (invoiceNumber: string, record: Invoice) => (
				<Space direction="vertical" size="small">
					<Text strong>{invoiceNumber}</Text>
					{record.description && (
						<Text type="secondary" style={{ fontSize: "12px" }}>
							{record.description}
						</Text>
					)}
				</Space>
			),
		},
		{
			title: "Date",
			dataIndex: "invoiceDate",
			key: "invoiceDate",
			render: (date: string, record: Invoice) => (
				<Space direction="vertical" size="small">
					<Text>{new Date(date).toLocaleDateString()}</Text>
					{record.paidAt && (
						<Text type="secondary" style={{ fontSize: "12px" }}>
							Paid: {new Date(record.paidAt).toLocaleDateString()}
						</Text>
					)}
				</Space>
			),
		},
		{
			title: "Amount",
			key: "amount",
			render: (record: Invoice) => (
				<Space direction="vertical" size="small">
					<Text strong>
						${((record.total || record.amountPaid) / 100).toFixed(2)}{" "}
						{record.currency.toUpperCase()}
					</Text>
					{record.lineItems && record.lineItems.length > 0 && (
						<Text type="secondary" style={{ fontSize: "12px" }}>
							{record.lineItems[0].quantity} location
							{record.lineItems[0].quantity !== 1 ? "s" : ""} × $
							{(record.lineItems[0].unitAmount / 100).toFixed(2)}
						</Text>
					)}
					{record.paymentMethod && (
						<Text type="secondary" style={{ fontSize: "12px" }}>
							{record.paymentMethod.brand?.toUpperCase()} ••••{" "}
							{record.paymentMethod.last4}
						</Text>
					)}
				</Space>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string) => {
				const getStatusConfig = (status: string) => {
					switch (status) {
						case "paid":
							return { color: "green", text: "PAID" };
						case "open":
							return { color: "orange", text: "PENDING" };
						case "draft":
							return { color: "blue", text: "DRAFT" };
						case "uncollectible":
							return { color: "red", text: "FAILED" };
						case "void":
							return { color: "gray", text: "VOID" };
						default:
							return { color: "default", text: status.toUpperCase() };
					}
				};

				const config = getStatusConfig(status);
				return <Tag color={config.color}>{config.text}</Tag>;
			},
		},
		{
			title: "Actions",
			key: "actions",
			render: (record: Invoice) => (
				<Space direction="vertical" size="small">
					<Space>
						{record.hostedInvoiceUrl && (
							<Button
								size="small"
								type="link"
								href={record.hostedInvoiceUrl}
								target="_blank"
								icon={<LinkOutlined />}
							>
								View
							</Button>
						)}
						{record.invoicePdf && (
							<Button
								size="small"
								type="link"
								href={record.invoicePdf}
								target="_blank"
								icon={<DownloadOutlined />}
							>
								PDF
							</Button>
						)}
					</Space>
					{record.lineItems &&
						record.lineItems.length > 0 &&
						record.lineItems[0].period && (
							<Text type="secondary" style={{ fontSize: "11px" }}>
								{new Date(
									record.lineItems[0].period.start,
								).toLocaleDateString()}{" "}
								-{" "}
								{new Date(record.lineItems[0].period.end).toLocaleDateString()}
							</Text>
						)}
				</Space>
			),
		},
	];

	if (loading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<Spin size="large" />
			</div>
		);
	}

	// All data loaded successfully

	// Show trial user message with plan selection
	if ((user?.is_trial || user?.isTrial) && !user?.isAccountDisabled) {
		return (
			<div style={{ padding: "24px" }}>
				<Alert
					message="Trial Account - Choose Your Plan"
					description="Your trial is ending soon. Choose a subscription plan to continue using FacilityAI's powerful features."
					type="warning"
					showIcon
					style={{ marginBottom: "24px" }}
				/>

				<Title level={3} style={{ marginBottom: "24px" }}>
					Choose Your Subscription Plan
				</Title>

				<Row gutter={[24, 24]} justify="center">
					{plans
						.filter((plan) => plan.name !== "Super Admin")
						.map((plan) => (
							<Col xs={24} sm={20} lg={16} xl={12} key={plan.id}>
								<Card
									hoverable
									style={{
										border: plan.isPopular
											? "2px solid #faad14"
											: "1px solid #d9d9d9",
										borderRadius: "12px",
										position: "relative",
										maxWidth: "800px",
										margin: "0 auto",
									}}
									bodyStyle={{
										padding: "20px",
									}}
								>
									{plan.isPopular && (
										<div
											style={{
												position: "absolute",
												top: "-8px",
												left: "50%",
												transform: "translateX(-50%)",
												background: "#faad14",
												color: "white",
												padding: "1px 8px",
												borderRadius: "4px",
												fontSize: "8px",
												fontWeight: "bold",
												zIndex: 2,
												whiteSpace: "nowrap",
												textTransform: "uppercase",
												lineHeight: "1",
												height: "12px",
												display: "flex",
												alignItems: "center",
											}}
										>
											MOST POPULAR
										</div>
									)}

									<Row gutter={[24, 16]} align="middle">
										{/* Left side - Plan info and pricing */}
										<Col xs={24} md={10}>
											<div style={{ textAlign: "center" }}>
												<Title
													level={3}
													style={{
														margin: 0,
														color: plan.isPopular ? "#faad14" : undefined,
													}}
												>
													{plan.name}
												</Title>
												<div style={{ margin: "12px 0" }}>
													{/* Display per-location pricing for paid plans */}
													{plan.name !== "Super Admin" && locationCount ? (
														(() => {
															const pricing = calculatePlanPricing(plan);
															return (
																<>
																	{/* Total monthly cost */}
																	<Text
																		style={{
																			fontSize: "32px",
																			fontWeight: "bold",
																			color: "#1890ff",
																		}}
																	>
																		{pricing.formattedTotalPrice}
																	</Text>
																	<Text
																		style={{ fontSize: "14px", color: "#666" }}
																	>
																		/{plan.interval}
																	</Text>
																	{/* Breakdown: X locations × $price per location */}
																	<div style={{ marginTop: "4px" }}>
																		<Text
																			style={{
																				fontSize: "12px",
																				color: "#999",
																			}}
																		>
																			{locationCount} location
																			{locationCount !== 1 ? "s" : ""} ×{" "}
																			{pricing.formattedPricePerLocation}
																		</Text>
																	</div>
																</>
															);
														})()
													) : (
														<>
															{/* Fallback or free plan display */}
															<Text
																style={{
																	fontSize: "32px",
																	fontWeight: "bold",
																	color: "#1890ff",
																}}
															>
																${(plan.price / 100).toFixed(0)}
															</Text>
															<Text style={{ fontSize: "14px", color: "#666" }}>
																/{plan.interval}
															</Text>
														</>
													)}
												</div>
												<Text type="secondary">{plan.description}</Text>
											</div>
										</Col>

										{/* Middle - Features */}
										<Col xs={24} md={10}>
											<div>
												<Text
													strong
													style={{ marginBottom: "8px", display: "block" }}
												>
													What's included:
												</Text>
												<div
													style={{
														display: "grid",
														gridTemplateColumns: "1fr 1fr",
														gap: "4px",
													}}
												>
													{plan.features.map((feature, index) => (
														<div
															key={index}
															style={{
																display: "flex",
																alignItems: "center",
																fontSize: "13px",
															}}
														>
															<span
																style={{
																	color: "#52c41a",
																	marginRight: "6px",
																	fontSize: "12px",
																}}
															>
																✓
															</span>
															<Text style={{ fontSize: "13px" }}>
																{feature}
															</Text>
														</div>
													))}
												</div>

												<div
													style={{
														marginTop: "12px",
														display: "flex",
														gap: "16px",
														fontSize: "12px",
													}}
												>
													<span>
														<Text strong>Users:</Text>{" "}
														{plan.maxUsers ? `${plan.maxUsers}` : "Unlimited"}
													</span>
													<span>
														<Text strong>Locations:</Text>{" "}
														{plan.maxLocations
															? `${plan.maxLocations}`
															: "Unlimited"}
													</span>
													<span>
														<Text strong>Storage:</Text>{" "}
														{plan.maxStorage
															? `${(plan.maxStorage / (1024 * 1024 * 1024)).toFixed(0)}GB`
															: "Unlimited"}
													</span>
												</div>
											</div>
										</Col>
									</Row>

									<Button
										type={plan.isPopular ? "primary" : "default"}
										size="large"
										block
										icon={<CrownOutlined />}
										onClick={() => handleSelectPlan(plan)}
										style={{
											height: "48px",
											fontSize: "16px",
											fontWeight: "bold",
											borderRadius: "8px",
										}}
									>
										{plan.name === "FacilityAI Base Subscription"
											? "SELECT BASE PLAN"
											: plan.name === "FacilityAI Plus Subscription"
												? "SELECT PLUS PLAN"
												: `SELECT ${plan.name.toUpperCase()}`}
									</Button>
								</Card>
							</Col>
						))}
				</Row>

				<div style={{ marginTop: "24px", textAlign: "center" }}>
					<Text type="secondary">
						All plans include our core features with 24/7 support. You can
						change or cancel your plan at any time.
					</Text>
				</div>

				{/* Subscription Modal for Trial Users */}
				{subscriptionModalOpen && (
					<SubscriptionModal
						open={subscriptionModalOpen}
						onClose={() => setSubscriptionModalOpen(false)}
						plan={selectedPlan}
						onSuccess={handleSubscriptionSuccess}
					/>
				)}
			</div>
		);
	}

	// Show no subscription message or error state
	if (!subscription) {
		return (
			<div style={{ padding: "24px" }}>
				<Alert
					message="No Active Subscription"
					description="You don't have an active subscription. Choose a plan to get started."
					type="warning"
					showIcon
					style={{ marginBottom: "24px" }}
				/>

				<Title level={3} style={{ marginBottom: "24px" }}>
					Choose Your Subscription Plan
				</Title>

				<Row gutter={[24, 24]} justify="center">
					{plans
						.filter((plan) => plan.name !== "Super Admin")
						.map((plan) => (
							<Col xs={24} sm={20} lg={16} xl={12} key={plan.id}>
								<Card
									hoverable
									style={{
										border: plan.isPopular
											? "2px solid #faad14"
											: "1px solid #d9d9d9",
										borderRadius: "12px",
										position: "relative",
										maxWidth: "800px",
										margin: "0 auto",
									}}
									bodyStyle={{
										padding: "20px",
									}}
								>
									{plan.isPopular && (
										<div
											style={{
												position: "absolute",
												top: "-8px",
												left: "50%",
												transform: "translateX(-50%)",
												background: "#faad14",
												color: "white",
												padding: "1px 8px",
												borderRadius: "4px",
												fontSize: "8px",
												fontWeight: "bold",
												zIndex: 2,
												whiteSpace: "nowrap",
												textTransform: "uppercase",
												lineHeight: "1",
												height: "12px",
												display: "flex",
												alignItems: "center",
											}}
										>
											MOST POPULAR
										</div>
									)}

									<Row gutter={[24, 16]} align="middle">
										{/* Left side - Plan info and pricing */}
										<Col xs={24} md={10}>
											<div style={{ textAlign: "center" }}>
												<Title
													level={3}
													style={{
														margin: 0,
														color: plan.isPopular ? "#faad14" : undefined,
													}}
												>
													{plan.name}
												</Title>
												<div style={{ margin: "12px 0" }}>
													{/* Display per-location pricing for paid plans */}
													{plan.name !== "Super Admin" && locationCount ? (
														(() => {
															const pricing = calculatePlanPricing(plan);
															return (
																<>
																	{/* Total monthly cost */}
																	<Text
																		style={{
																			fontSize: "32px",
																			fontWeight: "bold",
																			color: "#1890ff",
																		}}
																	>
																		{pricing.formattedTotalPrice}
																	</Text>
																	<Text
																		style={{ fontSize: "14px", color: "#666" }}
																	>
																		/{plan.interval}
																	</Text>
																	{/* Breakdown: X locations × $price per location */}
																	<div style={{ marginTop: "4px" }}>
																		<Text
																			style={{
																				fontSize: "12px",
																				color: "#999",
																			}}
																		>
																			{locationCount} location
																			{locationCount !== 1 ? "s" : ""} ×{" "}
																			{pricing.formattedPricePerLocation}
																		</Text>
																	</div>
																</>
															);
														})()
													) : (
														<>
															{/* Fallback or free plan display */}
															<Text
																style={{
																	fontSize: "32px",
																	fontWeight: "bold",
																	color: "#1890ff",
																}}
															>
																${(plan.price / 100).toFixed(0)}
															</Text>
															<Text style={{ fontSize: "14px", color: "#666" }}>
																/{plan.interval}
															</Text>
														</>
													)}
												</div>
												<Text type="secondary">{plan.description}</Text>
											</div>
										</Col>

										{/* Middle - Features */}
										<Col xs={24} md={10}>
											<div>
												<Text
													strong
													style={{ marginBottom: "8px", display: "block" }}
												>
													What's included:
												</Text>
												<div
													style={{
														display: "grid",
														gridTemplateColumns: "1fr 1fr",
														gap: "4px",
													}}
												>
													{plan.features.map((feature, index) => (
														<div
															key={index}
															style={{
																display: "flex",
																alignItems: "center",
																fontSize: "13px",
															}}
														>
															<span
																style={{
																	color: "#52c41a",
																	marginRight: "6px",
																	fontSize: "12px",
																}}
															>
																✓
															</span>
															<Text style={{ fontSize: "13px" }}>
																{feature}
															</Text>
														</div>
													))}
												</div>

												<div
													style={{
														marginTop: "12px",
														display: "flex",
														gap: "16px",
														fontSize: "12px",
													}}
												>
													<span>
														<Text strong>Users:</Text>{" "}
														{plan.maxUsers ? `${plan.maxUsers}` : "Unlimited"}
													</span>
													<span>
														<Text strong>Locations:</Text>{" "}
														{plan.maxLocations
															? `${plan.maxLocations}`
															: "Unlimited"}
													</span>
													<span>
														<Text strong>Storage:</Text>{" "}
														{plan.maxStorage
															? `${(plan.maxStorage / (1024 * 1024 * 1024)).toFixed(0)}GB`
															: "Unlimited"}
													</span>
												</div>
											</div>
										</Col>
									</Row>

									<Button
										type={plan.isPopular ? "primary" : "default"}
										size="large"
										block
										icon={<CrownOutlined />}
										onClick={() => handleSelectPlan(plan)}
										style={{
											height: "48px",
											fontSize: "16px",
											fontWeight: "bold",
											borderRadius: "8px",
										}}
									>
										{plan.name === "FacilityAI Base Subscription"
											? "SELECT BASE PLAN"
											: plan.name === "FacilityAI Plus Subscription"
												? "SELECT PLUS PLAN"
												: `SELECT ${plan.name.toUpperCase()}`}
									</Button>
								</Card>
							</Col>
						))}
				</Row>

				<div style={{ marginTop: "24px", textAlign: "center" }}>
					<Text type="secondary">
						All plans include our core features with 24/7 support. You can
						change or cancel your plan at any time.
					</Text>
				</div>

				{/* Subscription Modal for Non-Trial Users */}
				{subscriptionModalOpen && (
					<SubscriptionModal
						open={subscriptionModalOpen}
						onClose={() => setSubscriptionModalOpen(false)}
						plan={selectedPlan}
						onSuccess={handleSubscriptionSuccess}
					/>
				)}
			</div>
		);
	}

	const currentPlan = getCurrentPlan();
	const isSuperAdmin = currentPlan?.name === "Super Admin";

	return (
		<div style={{ padding: "24px" }}>
			<Title level={2}>Subscription Management</Title>

			{/* Current Subscription Card */}
			<Card
				title={
					<Space>
						<CrownOutlined />
						<span>Current Subscription</span>
					</Space>
				}
				extra={
					!isSuperAdmin && (
						<Button
							icon={<EditOutlined />}
							onClick={handleChangePlan}
							loading={actionLoading}
						>
							Change Plan
						</Button>
					)
				}
				style={{ marginBottom: "24px" }}
			>
				<Row gutter={[24, 24]}>
					<Col xs={24} md={12}>
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<div>
								<Text strong>Plan: </Text>
								<Text>{currentPlan?.name || "Unknown Plan"}</Text>
							</div>
							<div>
								<Text strong>Status: </Text>
								<Tag color={getStatusColor(subscription.status)}>
									{subscription.status.toUpperCase()}
								</Tag>
							</div>
							<div>
								<Text strong>Billing Cycle: </Text>
								<Text>
									{currentPlan?.interval === "month" ? "Monthly" : "Yearly"}
								</Text>
							</div>
							<div>
								<Text strong>Amount: </Text>
								<Text>
									{isSuperAdmin
										? "Free (Unlimited)"
										: (() => {
												if (currentPlan && locationCount) {
													const pricing = calculatePlanPricing(currentPlan);
													return `${pricing.formattedTotalPrice}/month (${locationCount} location${locationCount !== 1 ? "s" : ""} × ${pricing.formattedPricePerLocation})`;
												}
												return `$${(currentPlan?.price || 0) / 100}/month`;
											})()}
								</Text>
							</div>
						</Space>
					</Col>
					<Col xs={24} md={12}>
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<div>
								<Text strong>Current Period: </Text>
								<Text>
									{new Date(
										subscription.currentPeriodStart,
									).toLocaleDateString()}{" "}
									-{" "}
									{new Date(subscription.currentPeriodEnd).toLocaleDateString()}
								</Text>
							</div>
							<div>
								<Text strong>Next Billing Date: </Text>
								<Text>
									{new Date(subscription.currentPeriodEnd).toLocaleDateString()}
								</Text>
							</div>
							{subscription.cancelAtPeriodEnd && (
								<div>
									<Text type="warning" strong>
										Subscription will cancel at the end of the current period
									</Text>
								</div>
							)}
						</Space>
					</Col>
				</Row>

				<Divider />

				{/* Renewal-Based Billing Information */}
				{!isSuperAdmin && currentPlan && locationCount && (
					<Alert
						message="Per-Location Billing at Renewal"
						description={
							<div>
								<Text>
									Your subscription billing is updated at each renewal based on
									your current location count.
								</Text>
								<br />
								<Text type="secondary">
									• Adding/removing locations: No immediate charge or credit
								</Text>
								<br />
								<Text type="secondary">
									• At renewal: Billed for current location count
								</Text>
								<br />
								<Text type="secondary">
									• Next renewal billing: {locationCount} location
									{locationCount !== 1 ? "s" : ""} × {(() => {
										const pricing = calculatePlanPricing(currentPlan);
										return pricing.formattedPricePerLocation;
									})()} = {(() => {
										const pricing = calculatePlanPricing(currentPlan);
										return pricing.formattedTotalPrice;
									})()} per month
								</Text>
							</div>
						}
						type="info"
						showIcon
						style={{ marginBottom: "16px" }}
					/>
				)}

				{!isSuperAdmin && (
					<Space>
						{subscription.cancelAtPeriodEnd ? (
							<Button
								type="primary"
								onClick={handleReactivateSubscription}
								loading={actionLoading}
							>
								Reactivate Subscription
							</Button>
						) : (
							<Button
								danger
								onClick={handleCancelSubscription}
								loading={actionLoading}
							>
								Cancel Subscription
							</Button>
						)}
					</Space>
				)}
			</Card>

			{/* Usage Card */}
			{usage && (
				<Card title="Usage & Limits" style={{ marginBottom: "24px" }}>
					<Row gutter={[24, 24]}>
						<Col xs={24} md={8}>
							<div>
								<Text strong>Users</Text>
								<Progress
									percent={Math.round(
										((usage.currentUsers || usage.current_users || 0) /
											(usage.maxUsers || -1)) *
											100,
									)}
									format={() =>
										`${usage.currentUsers || usage.current_users || 0} / ${usage.maxUsers === -1 ? "∞" : usage.maxUsers || 0}`
									}
								/>
							</div>
						</Col>
						<Col xs={24} md={8}>
							<div>
								<Text strong>Locations</Text>
								<Progress
									percent={Math.round(
										((usage.currentLocations || usage.current_locations || 0) /
											(usage.maxLocations || -1)) *
											100,
									)}
									format={() =>
										`${usage.currentLocations || usage.current_locations || 0} / ${usage.maxLocations === -1 ? "∞" : usage.maxLocations || 0}`
									}
								/>
							</div>
						</Col>
						<Col xs={24} md={8}>
							<div>
								<Text strong>Storage</Text>
								<Progress
									percent={Math.round(
										((usage.storageUsed || usage.current_storage || 0) /
											(usage.storageLimit || usage.maxStorage || 1)) *
											100,
									)}
									format={() => {
										const used =
											usage.storageUsed || usage.current_storage || 0;
										const limit = usage.storageLimit || usage.maxStorage || 0;
										if (limit === -1)
											return `${(used / 1024 / 1024 / 1024).toFixed(1)}GB / ∞`;
										return `${(used / 1024 / 1024 / 1024).toFixed(1)}GB / ${(limit / 1024 / 1024 / 1024).toFixed(1)}GB`;
									}}
								/>
							</div>
						</Col>
					</Row>
				</Card>
			)}

			{/* Payment Methods Card */}
			<Card
				title={
					<Space>
						<CreditCardOutlined />
						<span>Payment Methods</span>
					</Space>
				}
				extra={
					!isSuperAdmin && (
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={handleAddPaymentMethod}
							loading={actionLoading}
						>
							Add Payment Method
						</Button>
					)
				}
				style={{ marginBottom: "24px" }}
			>
				{paymentMethods.length === 0 ? (
					<Text type="secondary">No payment methods on file</Text>
				) : (
					<Space direction="vertical" style={{ width: "100%" }}>
						{paymentMethods.map((method) => (
							<Card key={method.id} size="small">
								<Row justify="space-between" align="middle">
									<Col>
										<Space>
											<Text strong>
												{(
													method.card?.brand ||
													method.cardBrand ||
													"CARD"
												).toUpperCase()}{" "}
												•••• {method.card?.last4 || method.cardLast4}
											</Text>
											<Text type="secondary">
												Expires {method.card?.expMonth || method.cardExpMonth}/
												{method.card?.expYear || method.cardExpYear}
											</Text>
											{method.isDefault && <Tag color="blue">Default</Tag>}
										</Space>
									</Col>
									<Col>
										<Button
											size="small"
											danger
											loading={actionLoading}
											onClick={() => {
												confirm({
													title: "Remove Payment Method",
													content:
														"Are you sure you want to remove this payment method?",
													onOk: () =>
														handleRemovePaymentMethod(
															method.stripePaymentMethodId,
														),
												});
											}}
										>
											Remove
										</Button>
									</Col>
								</Row>
							</Card>
						))}
					</Space>
				)}
			</Card>

			{/* Billing History Card */}
			<Card
				title="Billing History"
				extra={
					<Text type="secondary" style={{ fontSize: "14px" }}>
						{invoices.length > 0
							? `${invoices.length} invoice${invoices.length !== 1 ? "s" : ""}`
							: "No invoices"}
					</Text>
				}
			>
				<Table
					columns={invoiceColumns}
					dataSource={invoices}
					rowKey="id"
					pagination={{ pageSize: 10 }}
					locale={{
						emptyText:
							"No billing history found. Invoices will appear here after your first payment.",
					}}
					size="middle"
				/>
			</Card>

			{/* Subscription Modal */}
			{subscriptionModalOpen && (
				<SubscriptionModal
					open={subscriptionModalOpen}
					onClose={() => setSubscriptionModalOpen(false)}
					plan={selectedPlan}
					onSuccess={handleSubscriptionSuccess}
				/>
			)}

			{/* Payment Method Modal */}
			<Modal
				title="Add Payment Method"
				open={paymentMethodModalOpen}
				onCancel={() => setPaymentMethodModalOpen(false)}
				footer={null}
				width={600}
			>
				<StripeProvider>
					<StripePaymentForm
						onPaymentMethodCreated={handlePaymentMethodAdded}
						onCancel={() => setPaymentMethodModalOpen(false)}
						loading={actionLoading}
					/>
				</StripeProvider>
			</Modal>

			{/* Plan Change Modal */}
			<Modal
				title="Change Subscription Plan"
				open={planChangeModalOpen}
				onCancel={() => setPlanChangeModalOpen(false)}
				footer={null}
				width={800}
			>
				<div style={{ padding: "16px 0" }}>
					<Text
						type="secondary"
						style={{ marginBottom: "16px", display: "block" }}
					>
						Select a new plan for your subscription. The change will take effect
						immediately.
					</Text>

					<Row gutter={[16, 16]}>
						{plans
							.filter((plan) => plan.name !== "Super Admin")
							.map((plan) => {
								const currentPlan = getCurrentPlan();
								const isCurrentPlan = currentPlan?.id === plan.id;
								const pricing = calculatePlanPricing(plan);

								return (
									<Col xs={24} sm={12} key={plan.id}>
										<Card
											hoverable={!isCurrentPlan}
											style={{
												border: isCurrentPlan
													? "2px solid #1890ff"
													: "1px solid #d9d9d9",
												opacity: isCurrentPlan ? 0.7 : 1,
											}}
											actions={
												isCurrentPlan
													? [
															<Text key="current" type="secondary">
																Current Plan
															</Text>,
														]
													: [
															<Button
																key="select"
																type="primary"
																onClick={() => handlePlanChange(plan)}
																loading={actionLoading}
															>
																Select {plan.name}
															</Button>,
														]
											}
										>
											<Card.Meta
												avatar={
													<CrownOutlined
														style={{ fontSize: "24px", color: "#1890ff" }}
													/>
												}
												title={
													<Space>
														<span>{plan.name}</span>
														{isCurrentPlan && <Tag color="blue">Current</Tag>}
													</Space>
												}
												description={
													<Space
														direction="vertical"
														size="small"
														style={{ width: "100%" }}
													>
														<Text strong style={{ fontSize: "18px" }}>
															{pricing.formattedTotalPrice}/month
														</Text>
														<Text type="secondary">
															{locationCount} location
															{locationCount !== 1 ? "s" : ""} ×{" "}
															{pricing.formattedPricePerLocation}
														</Text>
														{plan.description && (
															<Text
																type="secondary"
																style={{ fontSize: "12px" }}
															>
																{plan.description}
															</Text>
														)}
													</Space>
												}
											/>
										</Card>
									</Col>
								);
							})}
					</Row>
				</div>
			</Modal>
		</div>
	);
};
